import React, { useState, useEffect } from 'react';
import config from '../config';

interface Book {
  id: number;
  name: string;
  description: string | null;
  version: string;
  createdAt: string;
  updatedAt: string | null;
  metadata: BookMetadata[];
}

interface BookMetadata {
  id: number;
  bookId: number;
  key: string;
  value: string;
  rank: number;
  dateTime: string;
}

const BookManagement: React.FC = () => {
  const [books, setBooks] = useState<Book[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedBookId, setSelectedBookId] = useState<number | null>(null);
  const [selectedBook, setSelectedBook] = useState<Book | null>(null);
  const [editingMetadataId, setEditingMetadataId] = useState<number | null>(null);
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    version: '1.0'
  });
  
  const [metadataForm, setMetadataForm] = useState({
    key: '',
    value: '',
    rank: 0
  });

  useEffect(() => {
    fetchBooks();
  }, []);

  const fetchBooks = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/Books`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch books');
      }

      const data = await response.json();
      setBooks(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error fetching books');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/Books`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to create book');
      }

      await fetchBooks();
      setShowCreateModal(false);
      setFormData({ name: '', description: '', version: '1.0' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error creating book');
    }
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBook) return;
    
    setError(null);
    
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/Books/${selectedBook.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to update book');
      }

      await fetchBooks();
      setShowEditModal(false);
      setSelectedBook(null);
      setFormData({ name: '', description: '', version: '1.0' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error updating book');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this book?')) {
      return;
    }

    setError(null);
    
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/Books/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete book');
      }

      await fetchBooks();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error deleting book');
    }
  };

  const handleAddMetadata = async (bookId: number) => {
    if (!metadataForm.key.trim() || !metadataForm.value.trim()) {
      setError('Both key and value are required');
      return;
    }

    setError(null);
    
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/Books/${bookId}/metadata`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(metadataForm),
      });

      if (!response.ok) {
        throw new Error('Failed to add metadata');
      }

      await fetchBooks();
      setMetadataForm({ key: '', value: '', rank: 0 });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error adding metadata');
    }
  };

  const handleDeleteMetadata = async (metadataId: number) => {
    if (!window.confirm('Are you sure you want to delete this metadata?')) {
      return;
    }

    setError(null);
    
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/Books/metadata/${metadataId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete metadata');
      }

      await fetchBooks();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error deleting metadata');
    }
  };

  const openEditModal = (book: Book) => {
    setSelectedBook(book);
    setFormData({
      name: book.name,
      description: book.description || '',
      version: book.version
    });
    setShowEditModal(true);
  };

  const selectBook = (bookId: number) => {
    setSelectedBookId(bookId);
    const book = books.find(b => b.id === bookId);
    setSelectedBook(book || null);
    setEditingMetadataId(null); // Reset editing when selecting a new book
    setMetadataForm({ key: '', value: '', rank: 0 }); // Clear form
  };

  const startEditMetadata = (meta: BookMetadata) => {
    setEditingMetadataId(meta.id);
    setMetadataForm({
      key: meta.key,
      value: meta.value,
      rank: meta.rank
    });
  };

  const cancelEditMetadata = () => {
    setEditingMetadataId(null);
    setMetadataForm({ key: '', value: '', rank: 0 });
  };

  const handleUpdateMetadata = async (metadataId: number) => {
    if (!metadataForm.key.trim() || !metadataForm.value.trim()) {
      setError('Key and Value are required');
      return;
    }

    setError(null);
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/Books/metadata/${metadataId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          key: metadataForm.key,
          value: metadataForm.value,
          rank: metadataForm.rank
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update metadata');
      }

      await fetchBooks();
      setEditingMetadataId(null);
      setMetadataForm({ key: '', value: '', rank: 0 });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error updating metadata');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div style={{ padding: 24, width: '100%', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 32, fontWeight: 700, color: '#222', margin: 0 }}>
          📚 Book Management
        </h1>
        <button
          onClick={() => setShowCreateModal(true)}
          style={{
            padding: '12px 24px',
            backgroundColor: '#2196f3',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            fontSize: 16,
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1976d2'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2196f3'}
        >
          + Add New Book
        </button>
      </div>

      {error && (
        <div style={{
          padding: 16,
          backgroundColor: '#ffebee',
          color: '#c62828',
          borderRadius: 8,
          marginBottom: 24,
          border: '1px solid #ef5350'
        }}>
          ⚠️ {error}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#999' }}>
          Loading books...
        </div>
      ) : books.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: 48,
          backgroundColor: '#f5f5f5',
          borderRadius: 12,
          color: '#666'
        }}>
          <p style={{ fontSize: 18, marginBottom: 8 }}>No books found</p>
          <p style={{ fontSize: 14 }}>Create your first book to get started</p>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 24, minHeight: '600px' }}>
          {/* Left Side - Books List */}
          <div style={{ flex: '0 0 40%', display: 'flex', flexDirection: 'column', gap: 12, overflowY: 'auto', maxHeight: '80vh' }}>
            {books.map((book) => (
              <div
                key={book.id}
                onClick={() => selectBook(book.id)}
                style={{
                  backgroundColor: selectedBookId === book.id ? '#e3f2fd' : '#fff',
                  borderRadius: 12,
                  padding: 20,
                  border: selectedBookId === book.id ? '2px solid #1976d2' : '1px solid #e0e0e0',
                  boxShadow: selectedBookId === book.id ? '0 4px 12px rgba(25,118,210,0.15)' : '0 2px 8px rgba(0,0,0,0.08)',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (selectedBookId !== book.id) {
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedBookId !== book.id) {
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }
                }}
              >
                <div style={{ marginBottom: 12 }}>
                  <h3 style={{ fontSize: 18, fontWeight: 600, color: selectedBookId === book.id ? '#1976d2' : '#222', marginBottom: 8 }}>
                    {book.name}
                  </h3>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '4px 12px',
                      backgroundColor: selectedBookId === book.id ? '#1976d2' : '#e3f2fd',
                      color: selectedBookId === book.id ? '#fff' : '#1976d2',
                      borderRadius: 16,
                      fontSize: 12,
                      fontWeight: 600
                    }}>
                      v{book.version}
                    </span>
                    <span style={{ fontSize: 12, color: '#999' }}>
                      📎 {book.metadata?.length || 0} metadata
                    </span>
                  </div>
                  {book.description && (
                    <p style={{ color: '#666', fontSize: 13, marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                      {book.description}
                    </p>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditModal(book);
                    }}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#fff',
                      color: '#ff9800',
                      border: '1px solid #ff9800',
                      borderRadius: 6,
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#ff9800';
                      e.currentTarget.style.color = '#fff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#fff';
                      e.currentTarget.style.color = '#ff9800';
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(book.id);
                    }}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#fff',
                      color: '#f44336',
                      border: '1px solid #f44336',
                      borderRadius: 6,
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f44336';
                      e.currentTarget.style.color = '#fff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#fff';
                      e.currentTarget.style.color = '#f44336';
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Right Side - Metadata Panel */}
          <div style={{ flex: 1, backgroundColor: '#fff', borderRadius: 12, padding: 24, border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflowY: 'auto', maxHeight: '80vh' }}>
            {selectedBookId ? (
              (() => {
                const book = books.find(b => b.id === selectedBookId);
                if (!book) return null;
                
                return (
                  <>
                    <div style={{ marginBottom: 24, paddingBottom: 20, borderBottom: '2px solid #e0e0e0' }}>
                      <h2 style={{ fontSize: 24, fontWeight: 700, color: '#222', marginBottom: 8 }}>
                        {book.name}
                      </h2>
                      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '6px 14px',
                          backgroundColor: '#e3f2fd',
                          color: '#1976d2',
                          borderRadius: 16,
                          fontSize: 13,
                          fontWeight: 600
                        }}>
                          Version {book.version}
                        </span>
                      </div>
                      {book.description && (
                        <p style={{ color: '#666', fontSize: 14, marginBottom: 12 }}>
                          {book.description}
                        </p>
                      )}
                      <div style={{ fontSize: 12, color: '#999' }}>
                        Created: {formatDate(book.createdAt)}
                        {book.updatedAt && ` • Updated: ${formatDate(book.updatedAt)}`}
                      </div>
                    </div>

                    <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16, color: '#222' }}>
                      📋 Metadata Management
                    </h3>

                    {/* Add Metadata Form */}
                    <div style={{ marginBottom: 24, padding: 20, backgroundColor: '#f8f9fa', borderRadius: 8, border: '1px solid #dee2e6' }}>
                      <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#667eea' }}>
                        Add New Metadata
                      </h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: 4, fontSize: 12, fontWeight: 600, color: '#333' }}>
                            Key *
                          </label>
                          <input
                            type="text"
                            value={metadataForm.key}
                            onChange={(e) => setMetadataForm({ ...metadataForm, key: e.target.value })}
                            style={{
                              width: '100%',
                              padding: 10,
                              border: '1px solid #ced4da',
                              borderRadius: 6,
                              fontSize: 14,
                              boxSizing: 'border-box'
                            }}
                            placeholder="e.g., author, publisher, isbn"
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: 4, fontSize: 12, fontWeight: 600, color: '#333' }}>
                            Value (HTML supported) *
                          </label>
                          <textarea
                            value={metadataForm.value}
                            onChange={(e) => setMetadataForm({ ...metadataForm, value: e.target.value })}
                            rows={4}
                            style={{
                              width: '100%',
                              padding: 10,
                              border: '1px solid #ced4da',
                              borderRadius: 6,
                              fontSize: 14,
                              boxSizing: 'border-box',
                              fontFamily: 'monospace',
                              resize: 'vertical'
                            }}
                            placeholder="Enter value (HTML tags like <strong>, <em>, <ul>, <li> etc. are supported)"
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: 4, fontSize: 12, fontWeight: 600, color: '#333' }}>
                            Rank
                          </label>
                          <input
                            type="number"
                            value={metadataForm.rank}
                            onChange={(e) => setMetadataForm({ ...metadataForm, rank: parseInt(e.target.value) || 0 })}
                            style={{
                              width: '100%',
                              padding: 10,
                              border: '1px solid #ced4da',
                              borderRadius: 6,
                              fontSize: 14,
                              boxSizing: 'border-box'
                            }}
                            placeholder="0"
                          />
                        </div>
                        <button
                          onClick={() => handleAddMetadata(book.id)}
                          style={{
                            padding: '10px 20px',
                            backgroundColor: '#2196f3',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 6,
                            fontSize: 14,
                            fontWeight: 600,
                            cursor: 'pointer',
                            alignSelf: 'flex-start',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1976d2'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2196f3'}
                        >
                          + Add Metadata
                        </button>
                      </div>
                    </div>

                    {/* Metadata List */}
                    <div>
                      <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#222' }}>
                        Existing Metadata ({book.metadata?.length || 0})
                      </h4>
                      {book.metadata && book.metadata.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                          {book.metadata.map((meta) => (
                            <div
                              key={meta.id}
                              style={{
                                padding: 16,
                                backgroundColor: editingMetadataId === meta.id ? '#fff8e1' : '#fff',
                                borderRadius: 8,
                                border: editingMetadataId === meta.id ? '2px solid #ff9800' : '1px solid #dee2e6',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                              }}
                            >
                              {editingMetadataId === meta.id ? (
                                // Edit Mode
                                <>
                                  <div style={{ marginBottom: 12 }}>
                                    <label style={{ display: 'block', marginBottom: 4, fontSize: 12, fontWeight: 600, color: '#333' }}>
                                      Key *
                                    </label>
                                    <input
                                      type="text"
                                      value={metadataForm.key}
                                      onChange={(e) => setMetadataForm({ ...metadataForm, key: e.target.value })}
                                      style={{
                                        width: '100%',
                                        padding: 8,
                                        border: '1px solid #ced4da',
                                        borderRadius: 6,
                                        fontSize: 14,
                                        boxSizing: 'border-box'
                                      }}
                                    />
                                  </div>
                                  <div style={{ marginBottom: 12 }}>
                                    <label style={{ display: 'block', marginBottom: 4, fontSize: 12, fontWeight: 600, color: '#333' }}>
                                      Value (HTML supported) *
                                    </label>
                                    <textarea
                                      value={metadataForm.value}
                                      onChange={(e) => setMetadataForm({ ...metadataForm, value: e.target.value })}
                                      rows={4}
                                      style={{
                                        width: '100%',
                                        padding: 8,
                                        border: '1px solid #ced4da',
                                        borderRadius: 6,
                                        fontSize: 14,
                                        boxSizing: 'border-box',
                                        fontFamily: 'monospace',
                                        resize: 'vertical'
                                      }}
                                    />
                                  </div>
                                  <div style={{ marginBottom: 12 }}>
                                    <label style={{ display: 'block', marginBottom: 4, fontSize: 12, fontWeight: 600, color: '#333' }}>
                                      Rank
                                    </label>
                                    <input
                                      type="number"
                                      value={metadataForm.rank}
                                      onChange={(e) => setMetadataForm({ ...metadataForm, rank: parseInt(e.target.value) || 0 })}
                                      style={{
                                        width: '100%',
                                        padding: 8,
                                        border: '1px solid #ced4da',
                                        borderRadius: 6,
                                        fontSize: 14,
                                        boxSizing: 'border-box'
                                      }}
                                    />
                                  </div>
                                  <div style={{ display: 'flex', gap: 8 }}>
                                    <button
                                      onClick={() => handleUpdateMetadata(meta.id)}
                                      style={{
                                        padding: '6px 14px',
                                        backgroundColor: '#4caf50',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: 6,
                                        fontSize: 12,
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        transition: 'background-color 0.2s'
                                      }}
                                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#388e3c'}
                                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#4caf50'}
                                    >
                                      ✓ Save
                                    </button>
                                    <button
                                      onClick={cancelEditMetadata}
                                      style={{
                                        padding: '6px 14px',
                                        backgroundColor: '#fff',
                                        color: '#666',
                                        border: '1px solid #ccc',
                                        borderRadius: 6,
                                        fontSize: 12,
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = '#fff';
                                      }}
                                    >
                                      ✕ Cancel
                                    </button>
                                  </div>
                                </>
                              ) : (
                                // View Mode
                                <>
                                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                                    <div style={{ flex: 1 }}>
                                      <div style={{ marginBottom: 12 }}>
                                        <span style={{
                                          fontWeight: 600,
                                          color: '#1976d2',
                                          fontSize: 14,
                                          display: 'inline-block',
                                          padding: '4px 8px',
                                          backgroundColor: '#e3f2fd',
                                          borderRadius: 4
                                        }}>
                                          {meta.key}
                                        </span>
                                        <span style={{
                                          fontSize: 11,
                                          color: '#fff',
                                          backgroundColor: '#666',
                                          padding: '2px 8px',
                                          borderRadius: 12,
                                          marginLeft: 8,
                                          fontWeight: 600
                                        }}>
                                          Rank: {meta.rank}
                                        </span>
                                        <span style={{ fontSize: 11, color: '#999', marginLeft: 12 }}>
                                          {formatDate(meta.dateTime)}
                                        </span>
                                      </div>
                                      
                                      {/* Rendered HTML */}
                                      <div
                                        style={{
                                          color: '#333',
                                          fontSize: 14,
                                          lineHeight: 1.6,
                                          padding: '12px',
                                          backgroundColor: '#fff',
                                          borderRadius: 6,
                                          border: '1px solid #e0e0e0'
                                        }}
                                        dangerouslySetInnerHTML={{ __html: meta.value }}
                                      />
                                    </div>
                                    
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                      <button
                                        onClick={() => startEditMetadata(meta)}
                                        style={{
                                          padding: '4px 10px',
                                          backgroundColor: '#fff',
                                          color: '#ff9800',
                                          border: '1px solid #ff9800',
                                          borderRadius: 6,
                                          fontSize: 11,
                                          fontWeight: 600,
                                          cursor: 'pointer',
                                          transition: 'all 0.2s',
                                          whiteSpace: 'nowrap'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.backgroundColor = '#ff9800';
                                          e.currentTarget.style.color = '#fff';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.backgroundColor = '#fff';
                                          e.currentTarget.style.color = '#ff9800';
                                        }}
                                      >
                                        Edit
                                      </button>
                                      <button
                                        onClick={() => handleDeleteMetadata(meta.id)}
                                        style={{
                                          padding: '4px 10px',
                                          backgroundColor: '#fff',
                                          color: '#f44336',
                                          border: '1px solid #f44336',
                                          borderRadius: 6,
                                          fontSize: 11,
                                          fontWeight: 600,
                                          cursor: 'pointer',
                                          transition: 'all 0.2s',
                                          whiteSpace: 'nowrap'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.backgroundColor = '#f44336';
                                          e.currentTarget.style.color = '#fff';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.backgroundColor = '#fff';
                                          e.currentTarget.style.color = '#f44336';
                                        }}
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p style={{ textAlign: 'center', padding: 24, color: '#999', fontSize: 14, backgroundColor: '#f8f9fa', borderRadius: 8, border: '1px solid #dee2e6' }}>
                          No metadata yet. Add some using the form above.
                        </p>
                      )}
                    </div>
                  </>
                );
              })()
            ) : (
              <div style={{ textAlign: 'center', padding: 48, color: '#999' }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>📚</div>
                <p style={{ fontSize: 16, marginBottom: 8 }}>Select a book from the left</p>
                <p style={{ fontSize: 14 }}>to view and manage its metadata</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: 12,
            padding: 32,
            maxWidth: 500,
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h2 style={{ fontSize: 24, fontWeight: 600, marginBottom: 24, color: '#222' }}>
              Create New Book
            </h2>
            <form onSubmit={handleCreate}>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#333' }}>
                  Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  style={{
                    width: '100%',
                    padding: 12,
                    border: '1px solid #e0e0e0',
                    borderRadius: 8,
                    fontSize: 14,
                    boxSizing: 'border-box'
                  }}
                  placeholder="Enter book name"
                />
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#333' }}>
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  style={{
                    width: '100%',
                    padding: 12,
                    border: '1px solid #e0e0e0',
                    borderRadius: 8,
                    fontSize: 14,
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                    resize: 'vertical'
                  }}
                  placeholder="Enter book description"
                />
              </div>

              <div style={{ marginBottom: 24 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#333' }}>
                  Version
                </label>
                <input
                  type="text"
                  value={formData.version}
                  onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                  style={{
                    width: '100%',
                    padding: 12,
                    border: '1px solid #e0e0e0',
                    borderRadius: 8,
                    fontSize: 14,
                    boxSizing: 'border-box'
                  }}
                  placeholder="e.g., 1.0"
                />
              </div>

              <div style={{ display: 'flex', gap: 12 }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setFormData({ name: '', description: '', version: '1.0' });
                  }}
                  style={{
                    flex: 1,
                    padding: 12,
                    backgroundColor: '#f5f5f5',
                    color: '#666',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    padding: 12,
                    backgroundColor: '#2196f3',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Create Book
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedBook && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: 12,
            padding: 32,
            maxWidth: 500,
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h2 style={{ fontSize: 24, fontWeight: 600, marginBottom: 24, color: '#222' }}>
              Edit Book
            </h2>
            <form onSubmit={handleEdit}>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#333' }}>
                  Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  style={{
                    width: '100%',
                    padding: 12,
                    border: '1px solid #e0e0e0',
                    borderRadius: 8,
                    fontSize: 14,
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#333' }}>
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  style={{
                    width: '100%',
                    padding: 12,
                    border: '1px solid #e0e0e0',
                    borderRadius: 8,
                    fontSize: 14,
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ marginBottom: 24 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#333' }}>
                  Version
                </label>
                <input
                  type="text"
                  value={formData.version}
                  onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                  style={{
                    width: '100%',
                    padding: 12,
                    border: '1px solid #e0e0e0',
                    borderRadius: 8,
                    fontSize: 14,
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: 12 }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedBook(null);
                    setFormData({ name: '', description: '', version: '1.0' });
                  }}
                  style={{
                    flex: 1,
                    padding: 12,
                    backgroundColor: '#f5f5f5',
                    color: '#666',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    padding: 12,
                    backgroundColor: '#ff9800',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Update Book
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookManagement;
